import { refreshToken as refreshQuery } from '../queries/refreshtoken';
import { decodeToken } from './token';

// The originalApiCall will be an async function that takes an accessToken and returns a Response
type OriginalApiCall = (token: string) => Promise<Response>;

export async function withAuthRetry<T>(
    originalApiCall: OriginalApiCall,
    accessToken: string,
    decodeResponse: (bytes: Uint8Array) => T, // Function to decode the protobuf response
    setAccessTokenInContext: (token: string | null) => void, // Function to update AuthContext
    logout: () => void
): Promise<T> {
    let currentAccessToken = accessToken;
    const cognitoUsername = sessionStorage.getItem('cognitoUsername');

    // --- Initial Token Check: If token is missing, attempt refresh immediately ---
    if (!currentAccessToken) {
        console.warn("Access token is missing, attempting to refresh immediately...");
        if (!cognitoUsername) {
            logout();
            throw new Error("Unauthorized: No user ID found for token refresh.");
        }
        try {
            const refreshResponse = await refreshQuery(cognitoUsername);
            const newDecodedToken = decodeToken(refreshResponse.accessToken);
            if (newDecodedToken) {
                currentAccessToken = refreshResponse.accessToken; // Update token for subsequent fetch
                setAccessTokenInContext(currentAccessToken); // Update AuthContext
            } else {
                logout();
                throw new Error("Received invalid token from refresh endpoint");
            }
        } catch (refreshError) {
            console.error("Initial token refresh failed:", refreshError);
            logout();
            throw new Error("Unauthorized: Initial token refresh failed.");
        }
    }

    let response: Response | undefined;
    let retryCount = 0;
    const MAX_RETRIES = 1; // Only one retry after a token refresh

    while (retryCount <= MAX_RETRIES) {
        response = await originalApiCall(currentAccessToken);

        if (response.status === 401) {
            if (retryCount < MAX_RETRIES) {
                console.warn("Access token expired, attempting to refresh...");
                if (!cognitoUsername) {
                    logout();
                    throw new Error("Unauthorized: No user ID found for token refresh.");
                }
                try {
                    const refreshResponse = await refreshQuery(cognitoUsername);
                    const newDecodedToken = decodeToken(refreshResponse.accessToken);
                    if (newDecodedToken) {
                        currentAccessToken = refreshResponse.accessToken;
                        setAccessTokenInContext(currentAccessToken); // Update AuthContext
                        retryCount++;
                        continue; // Retry the fetch with the new token
                    } else {
                        logout();
                        throw new Error("Received invalid token from refresh endpoint");
                    }
                } catch (refreshError) {
                    console.error("Token refresh failed:", refreshError);
                    logout();
                    throw new Error("Unauthorized: Token refresh failed.");
                }
            } else {
                logout();
                throw new Error(`Unauthorized: Access token expired and refresh failed after ${MAX_RETRIES} retries.`);
            }
        }
        break; // Exit loop if not 401 or max retries reached
    }

    if (response!.status === 401) { // Check again after retry
        logout();
        throw new Error(`Unauthorized! Even after token refresh.`);
    }
    if (response!.status !== 200) {
        throw new Error(`API call failed! Status: ${response!.status}`);
    }

    const contentType = response!.headers.get("content-type");
    if (contentType && contentType.includes("application/json")) {
        // If it's JSON, we assume it's not a protobuf response that needs base64 decoding.
        // For void handlers (like finalizeReview), we can just return the result of the decoder with empty data key.
        return decodeResponse(new Uint8Array());
    }

    const encodedByteString = await response!.text();
    const decodedByteString = atob(encodedByteString);
    const decodedBytes = new Uint8Array(decodedByteString.length);
    for (let i = 0; i < decodedByteString.length; i++) {
        decodedBytes[i] = decodedByteString.charCodeAt(i);
    }

    return decodeResponse(decodedBytes);
}